import { AlertCircle, CheckCircle, ExternalLink } from 'lucide-react';
import { TagManagerConversionTag } from '@/lib/types';

const GTM_CONVERSION_TAG_HELP = 'https://support.google.com/tagmanager/answer/6105160';

const LINK_CLASS =
  'inline-flex items-center gap-2 text-sm text-[#290BD9] hover:text-[#2309C5] font-medium transition-colors duration-200';

const containerName = (container: TagManagerConversionTag['containers'][number]) =>
  container.public_id ? `${container.display_name} (${container.public_id})` : container.display_name;

const content = (check: TagManagerConversionTag) => {
  const conversionIds = check.conversion_ids.map((id) => `AW-${id}`).join(' or ');
  switch (check.state) {
    case 'published':
      return {
        success: true,
        title: 'Google Ads Conversion Tag Published',
        description: `Found in the live version of ${check.containers.map(containerName).join(', ')}.`,
      };
    case 'not_found':
      return {
        success: false,
        title: 'No Published Google Ads Conversion Tag',
        description: `No live Tag Manager container this account reaches has a Google Ads conversion tag for ${conversionIds}.`,
      };
    case 'checking':
      return {
        success: false,
        title: 'Checking Tag Manager',
        description: 'The published Tag Manager containers are still being read.',
      };
    default:
      return {
        success: false,
        title: 'Tag Manager Not Connected',
        description: 'Connect Tag Manager to check the Google Ads conversion tag is published.',
      };
  }
};

interface TagManagerConversionTagRowProps {
  check: TagManagerConversionTag;
  onOpenIntegrations: () => void;
}

/** The Tag Manager part of the conversion tracking check: is the Google Ads conversion tag published (#7280). */
export const TagManagerConversionTagRow = ({ check, onOpenIntegrations }: TagManagerConversionTagRowProps) => {
  const { success, title, description } = content(check);
  return (
    <div
      className={`border-l-4 rounded-lg p-4 ${
        success ? 'bg-[#D1FAE5] border-l-[#6EE7B7]' : 'bg-[#FEF3C7] border-l-[#FDE68A]'
      }`}
      data-testid="tag-manager-conversion-tag"
    >
      <div className="flex items-start gap-3">
        {success ? (
          <CheckCircle className="h-5 w-5 flex-shrink-0 text-[#047857]" />
        ) : (
          <AlertCircle className="h-5 w-5 flex-shrink-0 text-[#A16207]" />
        )}
        <div className="flex-1 space-y-2">
          <div>
            <p className="text-sm text-gray-500">Tag Manager</p>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-700">{description}</p>
          </div>
          {!success && check.state !== 'checking' && (
            <div className="flex justify-end gap-4">
              <button type="button" onClick={onOpenIntegrations} className={LINK_CLASS}>
                Open Integrations
              </button>
              <a href={GTM_CONVERSION_TAG_HELP} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
                More information
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
